/**
 * Converts between flat form state and nested model shape using modelPath mappings.
 */

/**
 * Extract values from a nested API response object into a flat form state.
 * Uses each field's modelPath to navigate the nested object.
 *
 * @param {Object} apiData - The nested API response data
 * @param {Array} fields - The field definitions with modelPath
 * @returns {Object} - Flat object keyed by fieldId
 */
export function fromModel(apiData, fields) {
    const formState = {};

    if (!apiData || !fields) {
        return formState;
    }

    fields.forEach((field) => {
        if (field.type === 'section') {
            return;
        }

        if (field.type === 'address') {
            // Address block has sub-fields mapped by convention
            const basePath = field.modelPath || 'address';
            formState[field.fieldId + '_address1'] = getNestedValue(apiData, basePath + '.address1') || '';
            formState[field.fieldId + '_address2'] = getNestedValue(apiData, basePath + '.address2') || '';
            formState[field.fieldId + '_city'] = getNestedValue(apiData, basePath + '.city') || '';
            formState[field.fieldId + '_state'] = getNestedValue(apiData, basePath + '.state') || '';
            formState[field.fieldId + '_zip'] = getNestedValue(apiData, basePath + '.zip') || '';
            formState[field.fieldId + '_country'] = getNestedValue(apiData, basePath + '.country') || '';
            return;
        }

        const path = field.modelPath || field.fieldId;
        const value = getNestedValue(apiData, path);

        if (value !== undefined && value !== null) {
            formState[field.fieldId] = value;
        } else {
            // Set default values based on type
            if (field.type === 'multiselect' || field.type === 'objectIdRefMulti') {
                formState[field.fieldId] = field.defaultValue || [];
            } else if (field.type === 'checkbox') {
                formState[field.fieldId] = field.defaultValue || false;
            } else if (field.type === 'number') {
                formState[field.fieldId] = field.defaultValue || '';
            } else {
                formState[field.fieldId] = field.defaultValue || '';
            }
        }
    });

    return formState;
}

/**
 * Build a nested object from flat form state for API submission.
 * Uses each field's modelPath to reconstruct the nested shape.
 *
 * @param {Object} formState - Flat object keyed by fieldId
 * @param {Array} fields - The field definitions with modelPath
 * @returns {Object} - Nested object matching API model shape
 */
export function toModel(formState, fields) {
    const result = {};

    if (!formState || !fields) {
        return result;
    }

    fields.forEach((field) => {
        if (field.type === 'section') {
            return;
        }

        if (field.type === 'address') {
            const basePath = field.modelPath || 'address';
            setNestedValue(result, basePath + '.address1', formState[field.fieldId + '_address1'] || '');
            setNestedValue(result, basePath + '.address2', formState[field.fieldId + '_address2'] || '');
            setNestedValue(result, basePath + '.city', formState[field.fieldId + '_city'] || '');
            setNestedValue(result, basePath + '.state', formState[field.fieldId + '_state'] || '');
            setNestedValue(result, basePath + '.zip', formState[field.fieldId + '_zip'] || '');
            setNestedValue(result, basePath + '.country', formState[field.fieldId + '_country'] || '');
            return;
        }

        const path = field.modelPath || field.fieldId;
        const value = formState[field.fieldId];

        if (value !== undefined) {
            setNestedValue(result, path, value);
        }
    });

    return result;
}

/**
 * Get a nested value from an object using a dot-notation path.
 * e.g., getNestedValue({ address: { city: 'NY' } }, 'address.city') => 'NY'
 */
function getNestedValue(obj, path) {
    if (!obj || !path) {
        return undefined;
    }
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length; i++) {
        if (current === null || current === undefined) {
            return undefined;
        }
        current = current[parts[i]];
    }
    return current;
}

/**
 * Set a nested value on an object using a dot-notation path.
 * e.g., setNestedValue({}, 'address.city', 'NY') => { address: { city: 'NY' } }
 */
function setNestedValue(obj, path, value) {
    if (!path) {
        return;
    }
    const parts = path.split('.');
    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
            current[parts[i]] = {};
        }
        current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
}
